import React, { FormEvent, useEffect, useState } from "react";
import { ISection } from "../../../../types/section";
// import useUpdatePage from '../../../../hooks/page/useUpdatePage';
// import useCreatePage from '../../../../hooks/page/useCreatePage';
// import useDeletePage from '../../../../hooks/page/useDeletePage';
import {
  IQuestion,
  QuestionDisplay,
  QuestionStyle,
  QuestionType,
} from "../../../../types/question";
import useUpdateQuestion from "../../../../hooks/question/useUpdateQuestion";
import useCreateQuestion from "../../../../hooks/question/useCreateQuestion";
import useDeleteQuestion from "../../../../hooks/question/useDeleteQuestion";
import { ICriteriaGroup } from "../../../../types/criteriaGroup";
import { IQuestionOption } from "../../../../types/questionOption";
import { IRepeat } from "../../../../types/repeat";
import { useQuestionnaireData } from "../../../../providers/QuestionnaireProvider";

interface IQuestionProps {
  sections: ISection[];
  currOption: number[];
  setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
  isDelete: boolean;
  setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  criteriaGroups: ICriteriaGroup[];
  questionOptions: IQuestionOption[];
  repeats: IRepeat[];
};

const Question = ({
  sections,
  currOption,
  setCurrOption,
  isDelete,
  setIsDelete,
  setIsOpenDeleteModal,
  criteriaGroups,
  questionOptions,
  repeats,
}: IQuestionProps) => {
  //eslint-disable-line

  const secIdx = currOption[0] || 0;
  const pageIdx = currOption[1] || 0;
  const questId = currOption[2] || 0;

  // const sectionId = sections[secIdx].id;
  const pageId = sections[secIdx].pages[pageIdx].id;
  const { questionnaireData } = useQuestionnaireData();
  const [question, setQuestion] = useState(sections[0].pages[0].questions[0]);
  const [title, setTitle] = useState<string>("");
  const [dayPastOption, setDayPastOption] = useState<number>(1);
  const [dayFutureOption, setDayFutureOption] = useState<number>(1);
  const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
  const [isServiceError, setIsServiceError] = useState<string | null>(null); //eslint-disable-line

  const blankQuestion: IQuestion = {
    questionnaire_code: questionnaireData?.quest_code ?? "MAIN-QUESTIONNAIRE",
    page_id: pageId ?? 0,
    order: 0,
    type: '' as QuestionType,
    question: "",
    criteria_group_id: undefined,
    question_option_id:
      question?.type?.includes("search") ||
        question?.type?.includes("dropdown") ||
        question?.type?.includes("radio") ||
        question?.type?.includes("checkbox") ||
        question?.type?.includes("select")
        ? questionOptions[0].id
        : undefined,
    repeat_id: undefined,
    comment: "",
    why_ask: "",
    help: "",
    code: "",
    placeholder:
      question?.type?.includes("text") ||
        question?.type?.includes("dropdown") ||
        question?.type?.includes("search") ||
        question?.type?.includes("select")
        ? ""
        : undefined,
    pattern_mask: question?.type?.includes("text") ? "" : undefined,
    validation_regex: question?.type?.includes("text") ? "" : undefined,
    answers_min: question?.type?.includes("_multi") ? 0 : undefined,
    answers_max: question?.type?.includes("_multi") ? 0 : undefined,
    day_past:
      question?.type?.includes("date") || question?.type?.includes("monthyear")
        ? 0
        : undefined,
    day_future:
      question?.type?.includes("date") || question?.type?.includes("monthyear")
        ? 0
        : undefined,
    limit_min: question?.type?.includes("text") ? 0 : undefined,
    limit_max: question?.type?.includes("text") ? 0 : undefined,
    style: QuestionStyle.PRIMARY,
    display: QuestionDisplay.NORMAL,
    page: {},
  };

  useEffect(() => {
    if (questId === sections[secIdx].pages[pageIdx].questions.length) {
      setQuestion({ ...blankQuestion });
      setTitle("New Question");
    } else {
      setQuestion(sections[secIdx].pages[pageIdx].questions[questId]);
      setTitle(sections[secIdx].pages[pageIdx].questions[questId].question);
    }
  }, [questId, sections]); //eslint-disable-line

  const { mutate: updateQuestion } = useUpdateQuestion({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: createQuestion } = useCreateQuestion({
    setIsProcessRunning,
    setIsServiceError,
  });
  const { mutate: deleteQuestion } = useDeleteQuestion({
    setIsProcessRunning,
    setIsServiceError,
  });

  useEffect(() => {
    if (isDelete) {
      setCurrOption([secIdx, pageIdx, 0]);
      if (question.id) deleteQuestion(question.id);
      setIsDelete(false);
    }
  }, [isDelete]); //eslint-disable-line

  if (!question) {
    return <p>Loading...</p>;
  }

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      id: question.id,
      criteria_group_id: question.criteria_group_id ?? null,
      question_option_id: question.question_option_id ?? null,
      repeat_id: question.repeat_id ?? null,
      page_id: question.page_id,
      answers_min: question.answers_min,
      answers_max: question.answers_max,
      day_past: question.day_past ? question.day_past : 0,
      day_future: question.day_future ? question.day_future : 0,
      limit_min: question.limit_min,
      limit_max: question.limit_max,
      question_paragraph: question.question_paragraph,
      why_ask: question.why_ask,
      validation_regex: question.validation_regex,
      pattern_mask: question.pattern_mask,
      style: question.style,
      comment: question.comment,
      help: question.help,
      placeholder: question.placeholder,
      question: question.question,
      code: question.code,
      type: question.type,
      questionnaire_code: questionnaireData?.quest_code ?? "MAIN-QUESTIONNAIRE",
      display: question.display,
    };
    if (questId === sections[secIdx].pages[pageIdx].questions.length) {
      createQuestion(payload);
    } else {
      updateQuestion(payload);
    }
    setTitle(question.question);
  };

  const handleDelete = () => {
    if (questId === sections[secIdx].pages[pageIdx].questions.length) {
      if (!questId) {
        setCurrOption([secIdx, pageIdx]);
      } else {
        setCurrOption([secIdx, pageIdx, 0]);
      }
      setQuestion(sections[secIdx].pages[pageIdx].questions[questId]);
    } else {
      setIsOpenDeleteModal(true);
    }
  };

  return (
    <div className="imm_quest_admin__content">
      <div className="content_head">
        <h3>{title}</h3>
      </div>

      <form onSubmit={handleSave} className="content_form">
        <div className="field">
          <label htmlFor="question">
            Question <span className="imm_quest_admin__required_field">*</span>
          </label>
          <input
            id="question"
            name="question"
            placeholder="Enter Question"
            value={question.question}
            onChange={(e) =>
              setQuestion({ ...question, question: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="code">
            Code <span className="imm_quest_admin__required_field">*</span>
          </label>
          <input
            id="code"
            name="code"
            disabled={question.id !== undefined}
            placeholder="Enter Code"
            value={question.code}
            onChange={(e) => setQuestion({ ...question, code: e.target.value })}
          />
        </div>

        {criteriaGroups && criteriaGroups.length > 0 && (
          <div className="field">
            <label htmlFor="criteriaGroup">Criteria Group</label>
            <select
              id="criteriaGroup"
              name="criteriaGroup"
              value={question.criteria_group_id || ""}
              onChange={(e) =>
                setQuestion({
                  ...question,
                  criteria_group_id: parseInt(e.target.value),
                })
              }
            >
              <option value={""}>-- Select criteria group --</option>
              {criteriaGroups.map((criteriaGroup) => (
                <option key={criteriaGroup.id} value={criteriaGroup.id}>
                  {criteriaGroup.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {repeats && repeats.length > 0 && (
          <div className="field">
            <label htmlFor="repeat">Repeat</label>
            <select
              id="repeat"
              name="repeat"
              value={question.repeat_id || ""}
              onChange={(e) =>
                setQuestion({
                  ...question,
                  repeat_id: parseInt(e.target.value) || undefined,
                })
              }
            >
              <option value="">-- Select repeat --</option>
              {repeats.map((repeat) => (
                <option key={repeat.id} value={repeat.id}>
                  {repeat.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="field">
          <label htmlFor="type">
            Type <span className="imm_quest_admin__required_field">*</span>
          </label>
          <select
            id="type"
            name="type"
            disabled={question.id !== undefined}
            defaultValue={''}
            value={question.type || ''}
            onChange={(e) =>
              setQuestion({
                ...question,
                type: e.target.value as QuestionType,
              })
            }
          >
            <option value={''}>-- Select type --</option>
            <option value={QuestionType.Date}>Date</option>
            <option value={QuestionType.MonthYear}>Month Year</option>
            <option value={QuestionType.Boolean}>Boolean</option>
            <option value={QuestionType.Select}>Select</option>
            <option value={QuestionType.Text}>Text</option>
            <option value={QuestionType.Radio}>Radio</option>
            <option value={QuestionType.Checkbox}>Checkbox</option>
            <option value={QuestionType.DropdownSingle}>Dropdown Single</option>
            <option value={QuestionType.DropdownMulti}>Dropdown Multi</option>
            <option value={QuestionType.SearchSingle}>Search Single</option>
            {/* <option value={QuestionType.SearchMulti}>Search Multi</option> */}
            <option value={QuestionType.SearchSingleMultiInstance}>
              Search Single Multi Instance
            </option>
            {/* <option value={QuestionType.SearchMultiMultiInstance}>
              Search Multi Multi Instance
            </option> */}
          </select>
        </div>

        {(question?.type?.includes("search") ||
          question?.type?.includes("dropdown") ||
          question?.type?.includes("radio") ||
          question?.type?.includes("checkbox") ||
          question?.type?.includes("select")) &&
          questionOptions &&
          questionOptions.length > 0 && (
            <div className="field">
              <label htmlFor="questionOption">Question Option</label>
              <select
                id="questionOption"
                name="questionOption"
                value={question.question_option_id || undefined}
                onChange={(e) =>
                  setQuestion({
                    ...question,
                    question_option_id: parseInt(e.target.value),
                  })
                }
              >
                <option value={undefined}>-- Select question option --</option>
                {questionOptions.map((questionOption) => (
                  <option key={questionOption.id} value={questionOption.id}>
                    {questionOption.type}
                  </option>
                ))}
              </select>
            </div>
          )}

        <div className="field">
          <label htmlFor="question_paragraph">Question Paragraph</label>
          <textarea
            id="question_paragraph"
            name="question_paragraph"
            placeholder="Enter Question Paragraph"
            value={question.question_paragraph || ""}
            onChange={(e) =>
              setQuestion({ ...question, question_paragraph: e.target.value })
            }
          ></textarea>
        </div>

        <div className="field">
          <label htmlFor="comment">Comment</label>
          <input
            id="comment"
            name="comment"
            placeholder="Enter Comment"
            value={question.comment}
            onChange={(e) =>
              setQuestion({ ...question, comment: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="why_ask">Why Ask</label>
          <input
            id="why_ask"
            name="why_ask"
            placeholder="Enter Why Ask"
            value={question.why_ask}
            onChange={(e) =>
              setQuestion({ ...question, why_ask: e.target.value })
            }
          />
        </div>

        <div className="field">
          <label htmlFor="help">Help</label>
          <input
            id="help"
            name="help"
            placeholder="Enter Help"
            value={question.help}
            onChange={(e) => setQuestion({ ...question, help: e.target.value })}
          />
        </div>

        {(question?.type?.includes("text") ||
          question?.type?.includes("dropdown") ||
          question?.type?.includes("search") ||
          question?.type?.includes("select")) && (
            <div className="field">
              <label htmlFor="placeholder">Placeholder</label>
              <input
                id="placeholder"
                name="placeholder"
                placeholder="Enter Placeholder"
                value={question.placeholder}
                onChange={(e) =>
                  setQuestion({ ...question, placeholder: e.target.value })
                }
              />
            </div>
          )}

        {question?.type?.includes("text") && (
          <div className="field">
            <label htmlFor="pattern_mask">Pattern Mask</label>
            <input
              id="pattern_mask"
              name="pattern_mask"
              placeholder="Enter Pattern Mask"
              value={question.pattern_mask}
              onChange={(e) =>
                setQuestion({ ...question, pattern_mask: e.target.value })
              }
            />
          </div>
        )}

        {question?.type?.includes("text") && (
          <div className="field">
            <label htmlFor="validation_regex">Validation Regex</label>
            <input
              id="validation_regex"
              name="validation_regex"
              placeholder="Enter Validation Regex"
              value={question.validation_regex}
              onChange={(e) =>
                setQuestion({ ...question, validation_regex: e.target.value })
              }
            />
          </div>
        )}

        {question?.type?.includes("text") && (
          <>
            <div className="field">
              <label htmlFor="limit_min">Minimum Limit</label>

              <input
                id="limit_min"
                name="limit_min"
                type="number"
                value={question.limit_min}
                onChange={(e) =>
                  setQuestion({
                    ...question,
                    limit_min: parseInt(e.target.value),
                  })
                }
              />
            </div>

            <div className="field">
              <label htmlFor="limit_max">Maximum Limit</label>
              <input
                id="limit_max"
                name="limit_max"
                type="number"
                value={question.limit_max}
                onChange={(e) =>
                  setQuestion({
                    ...question,
                    limit_max: parseInt(e.target.value),
                  })
                }
              />
            </div>
          </>
        )}

        {(question?.type?.includes("date") ||
          question?.type?.includes("monthyear")) && (
            <>
              <div className="fields">
                <label htmlFor="day_past">
                  Days Past{" "}
                  <span className="imm_quest_admin__required_field">*</span>
                </label>
                <div className="field__days">
                  <input
                    id="day_past"
                    name="day_past"
                    type="number"
                    placeholder="0"
                    value={
                      question.day_past ? question.day_past / dayPastOption : ""
                    }
                    onChange={(e) => {
                      setQuestion({
                        ...question,
                        day_past: parseInt(e.target.value) * dayPastOption,
                      });
                    }}
                  />
                  <select
                    id="days_options"
                    name="days_options"
                    value={dayPastOption}
                    onChange={(e) => {
                      setQuestion({ ...question, day_past: 0 });
                      setDayPastOption(parseInt(e.target.value));
                    }}
                  >
                    <option value={1}>Days</option>
                    <option value={7}>Weeks</option>
                    <option value={30}>Months</option>
                    <option value={365}>Years</option>
                  </select>
                </div>
              </div>

              <div className="fields">
                <label htmlFor="day_future">
                  Days Future{" "}
                  <span className="imm_quest_admin__required_field">*</span>
                </label>
                <div className="field__days">
                  <input
                    id="day_future"
                    name="day_future"
                    type="number"
                    placeholder="0"
                    value={
                      question.day_future
                        ? question.day_future / dayFutureOption
                        : ""
                    }
                    onChange={(e) => {
                      setQuestion({
                        ...question,
                        day_future: parseInt(e.target.value) * dayFutureOption,
                      });
                    }}
                  />
                  <select
                    id="days_options"
                    name="days_options"
                    value={dayFutureOption}
                    onChange={(e) => {
                      setQuestion({ ...question, day_future: 0 });
                      setDayFutureOption(parseInt(e.target.value));
                    }}
                  >
                    <option value={1}>Days</option>
                    <option value={7}>Weeks</option>
                    <option value={30}>Months</option>
                    <option value={365}>Years</option>
                  </select>
                </div>
              </div>
            </>
          )}

        {question?.type?.includes("_multi") && (
          <>
            <div className="field">
              <label htmlFor="answers_min">
                Minimum Answers{" "}
                <span className="imm_quest_admin__required_field">*</span>
              </label>
              <input
                id="answers_min"
                name="answers_min"
                type="number"
                value={question.answers_min}
                onChange={(e) =>
                  setQuestion({
                    ...question,
                    answers_min: parseInt(e.target.value),
                  })
                }
              />
            </div>

            <div className="field">
              <label htmlFor="answers_max">
                Maximum Answers{" "}
                <span className="imm_quest_admin__required_field">*</span>
              </label>
              <input
                id="answers_max"
                name="answers_max"
                type="number"
                value={question.answers_max}
                onChange={(e) =>
                  setQuestion({
                    ...question,
                    answers_max: parseInt(e.target.value),
                  })
                }
              />
            </div>
          </>
        )}

        <div className="field">
          <label htmlFor="style">
            Style <span className="imm_quest_admin__required_field">*</span>
          </label>
          <select
            id="style"
            name="style"
            value={question.style}
            defaultValue={QuestionStyle.PRIMARY}
            onChange={(e) =>
              setQuestion({
                ...question,
                style: e.target.value as QuestionStyle,
              })
            }
          >
            <option value={QuestionStyle.PRIMARY}>Primary</option>
            <option value={QuestionStyle.SECONDARY}>Secondary</option>
            <option value={QuestionStyle.TERTIARY}>Tertiary</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="style">
            Display <span className="imm_quest_admin__required_field">*</span>
          </label>
          <select
            id="display"
            name="display"
            value={question.display}
            defaultValue={QuestionDisplay.NORMAL}
            onChange={(e) =>
              setQuestion({
                ...question,
                display: e.target.value as QuestionDisplay,
              })
            }
          >
            <option value={QuestionDisplay.NORMAL}>Normal</option>
            <option value={QuestionDisplay.READ_ONLY}>Read Only</option>
            <option value={QuestionDisplay.HIDDEN}>Hidden</option>
          </select>
        </div>

        <div className="actions">
          <button className="delete_btn" type="button" onClick={handleDelete}>
            {isProcessRunning ? (
              <i className="imm_quest_admin__process_loader" />
            ) : questId === sections[secIdx].pages[pageIdx].questions.length ? (
              "Cancel"
            ) : (
              "Delete"
            )}
          </button>
          <button className="save_btn" type="submit">
            {isProcessRunning ? (
              <i className="imm_quest_admin__process_loader" />
            ) : pageId === sections[secIdx].pages[pageIdx].questions.length ? (
              "Create"
            ) : (
              "Save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Question;
